/**
 * Resolves who can see a prioritization item.
 *
 * Mirrors the membership-based read model from migration 297:
 * workspace members, cycle/track participants (via cycle_item_participation),
 * and session participants for isolated session items.
 */

import { useEffect, useMemo, useRef } from 'react';
import { useCycleParticipationStore } from '@/stores/prioritization/cycleParticipationStore';
import { useCycleParticipantsStore } from '@/stores/prioritization/cycleParticipantsStore';
import { useCycleParticipantGroupsStore } from '@/stores/prioritization/cycleParticipantGroupsStore';
import { useTrackParticipantsStore } from '@/stores/prioritization/trackParticipantsStore';
import { useTrackParticipantGroupsStore } from '@/stores/prioritization/trackParticipantGroupsStore';
import { useSessionParticipantsStore } from '@/stores/prioritization/sessionParticipantsStore';
import { useSessionParticipantGroupsStore } from '@/stores/prioritization/sessionParticipantGroupsStore';
import { useDegapProcessStore } from '@/stores/prioritization/degapProcessStore';
import { useTrackStore } from '@/stores/prioritization/trackStore';
import { useSessionStore } from '@/stores/prioritization/sessionStore';
import { useGroupsStore } from '@/stores/prioritization/groupsStore';
import { useSystemUsers } from '@/hooks/useSystemUsers';

export interface VisibilityPerson {
  userId: string;
  role?: string;
}

export interface VisibilityGroup {
  groupId: string;
  groupName: string;
}

export interface ItemVisibilityTrackContext {
  trackId: string;
  trackName: string;
  trackOnlyPeople: VisibilityPerson[];
  trackOnlyGroups: VisibilityGroup[];
}

export interface ItemVisibilityCycleContext {
  cycleId: string;
  cycleName: string;
  cyclePeople: VisibilityPerson[];
  cycleGroups: VisibilityGroup[];
  tracks: ItemVisibilityTrackContext[];
}

export interface ItemVisibilitySessionContext {
  sessionId: string;
  sessionName: string;
  people: VisibilityPerson[];
  groups: VisibilityGroup[];
}

export interface ItemVisibilityData {
  loading: boolean;
  workspaceMemberCount: number;
  totalViewerCount: number;
  externalViewerCount: number;
  cycleContexts: ItemVisibilityCycleContext[];
  sessionContexts: ItemVisibilitySessionContext[];
  allViewerIds: string[];
}

function roleLabel(role: string | undefined): string {
  if (!role) return 'Participant';
  if (role === 'facilitator') return 'Facilitator';
  return role.charAt(0).toUpperCase() + role.slice(1).replace(/_/g, ' ');
}

/** Pure aggregation for tests and stable viewer counts (§2.4 proof-of-life). */
export function collectItemViewerIds(
  workspaceUserIds: string[],
  cycleContexts: ItemVisibilityCycleContext[],
  sessionContexts: ItemVisibilitySessionContext[],
): string[] {
  const ids = new Set<string>(workspaceUserIds);
  for (const cycle of cycleContexts) {
    for (const p of cycle.cyclePeople) ids.add(p.userId);
    for (const track of cycle.tracks) {
      for (const p of track.trackOnlyPeople) ids.add(p.userId);
    }
  }
  for (const session of sessionContexts) {
    for (const p of session.people) ids.add(p.userId);
  }
  return [...ids].sort();
}

export function useItemVisibility(itemId: string | undefined): ItemVisibilityData {
  const participationsRecord = useCycleParticipationStore((s) => s.participations);
  const participations = useMemo(
    () => (itemId ? Object.values(participationsRecord).filter((p) => p.itemId === itemId) : []),
    [participationsRecord, itemId],
  );

  const cycleIds = useMemo(() => {
    const ids = new Set<string>();
    for (const p of participations) {
      if (p.cycleStatus !== 'excluded') ids.add(p.cycleId);
    }
    return [...ids].sort();
  }, [participations]);

  const cycleIdsKey = cycleIds.join(',');

  const cycles = useDegapProcessStore((s) => s.cycles);
  const allTracks = useTrackStore((s) => s.tracks);
  const allSessions = useSessionStore((s) => s.sessions);
  const groups = useGroupsStore((s) => s.groups);

  const cycleParticipants = useCycleParticipantsStore((s) => s.participants);
  const cycleGroups = useCycleParticipantGroupsStore((s) => s.participantGroups);
  const trackParticipants = useTrackParticipantsStore((s) => s.participants);
  const trackGroups = useTrackParticipantGroupsStore((s) => s.participantGroups);
  const sessionParticipants = useSessionParticipantsStore((s) => s.participants);
  const sessionGroups = useSessionParticipantGroupsStore((s) => s.participantGroups);

  const { users: workspaceUsers, loading: usersLoading } = useSystemUsers();

  const workspaceUserIds = useMemo(() => workspaceUsers.map((u) => u.id), [workspaceUsers]);

  const isolatedSessionIds = useMemo(() => {
    if (!itemId) return [];
    return allSessions
      .filter((s) => (s.isolatedItems ?? []).includes(itemId))
      .map((s) => s.id)
      .sort();
  }, [allSessions, itemId]);

  const isolatedSessionIdsKey = isolatedSessionIds.join(',');

  const groupNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const g of groups) map.set(g.id, g.name);
    return map;
  }, [groups]);

  const fetchedKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!itemId) return;
    const fetchKey = `${itemId}|${cycleIdsKey}|${isolatedSessionIdsKey}`;
    if (fetchedKeyRef.current === fetchKey) return;
    fetchedKeyRef.current = fetchKey;

    const { fetchFromSupabase: fetchGroups } = useGroupsStore.getState();
    const { fetchParticipantsForCycle } = useCycleParticipantsStore.getState();
    const { fetchParticipantGroupsForCycle } = useCycleParticipantGroupsStore.getState();
    const { fetchParticipantsForTrack } = useTrackParticipantsStore.getState();
    const { fetchParticipantGroupsForTrack } = useTrackParticipantGroupsStore.getState();
    const { fetchParticipantsForSession } = useSessionParticipantsStore.getState();
    const { getTracksForCycle } = useTrackStore.getState();

    void fetchGroups();
    for (const cycleId of cycleIds) {
      void fetchParticipantsForCycle(cycleId);
      void fetchParticipantGroupsForCycle(cycleId);
      for (const track of getTracksForCycle(cycleId)) {
        void fetchParticipantsForTrack(track.id);
        void fetchParticipantGroupsForTrack(track.id);
      }
    }
    for (const sessionId of isolatedSessionIds) {
      void fetchParticipantsForSession(sessionId);
    }
  }, [itemId, cycleIdsKey, isolatedSessionIdsKey, cycleIds, isolatedSessionIds]);

  const cycleContexts = useMemo((): ItemVisibilityCycleContext[] => {
    return cycleIds.map((cycleId) => {
      const cycle = cycles.find((c) => c.id === cycleId);
      const cycleDirect = cycleParticipants.filter((p) => p.cycleId === cycleId && p.status === 'active');
      const cycleGroupRows = cycleGroups.filter((g) => g.cycleId === cycleId && g.status === 'active');

      const cyclePeople: VisibilityPerson[] = cycleDirect.map((p) => ({
        userId: p.userId,
        role: roleLabel(p.role),
      }));
      const cycleGroupList: VisibilityGroup[] = cycleGroupRows.map((g) => ({
        groupId: g.groupId,
        groupName: groupNameById.get(g.groupId) ?? 'Group',
      }));

      const cycleDirectIds = new Set(cycleDirect.map((p) => p.userId));
      const tracks = allTracks
        .filter((t) => t.cycleId === cycleId)
        .map((track) => {
          const trackDirect = trackParticipants.filter((p) => p.trackId === track.id && p.status === 'active');
          const trackGroupRows = trackGroups.filter((g) => g.trackId === track.id && g.status === 'active');
          const trackOnlyPeople = trackDirect
            .filter((p) => !cycleDirectIds.has(p.userId))
            .map((p) => ({ userId: p.userId, role: roleLabel(p.role) }));
          const trackOnlyGroups = trackGroupRows.map((g) => ({
            groupId: g.groupId,
            groupName: groupNameById.get(g.groupId) ?? 'Group',
          }));
          return {
            trackId: track.id,
            trackName: track.name,
            trackOnlyPeople,
            trackOnlyGroups,
          };
        });

      return {
        cycleId,
        cycleName: cycle?.name ?? 'Cycle',
        cyclePeople,
        cycleGroups: cycleGroupList,
        tracks,
      };
    });
  }, [cycleIds, cycles, cycleParticipants, cycleGroups, trackParticipants, trackGroups, allTracks, groupNameById]);

  const sessionContexts = useMemo((): ItemVisibilitySessionContext[] => {
    if (!itemId || isolatedSessionIds.length === 0) return [];
    const sessionById = new Map(allSessions.map((s) => [s.id, s]));
    return isolatedSessionIds.map((sessionId) => {
      const session = sessionById.get(sessionId);
      const direct = sessionParticipants.filter((p) => p.sessionId === sessionId && p.status === 'active');
      const groupRows = sessionGroups.filter((g) => g.sessionId === sessionId && g.status === 'active');
      return {
        sessionId,
        sessionName: session?.name ?? 'Session',
        people: direct.map((p) => ({ userId: p.userId, role: roleLabel(p.role) })),
        groups: groupRows.map((g) => ({
          groupId: g.groupId,
          groupName: groupNameById.get(g.groupId) ?? 'Group',
        })),
      };
    });
  }, [itemId, isolatedSessionIds, allSessions, sessionParticipants, sessionGroups, groupNameById]);

  const allViewerIds = useMemo(
    () => collectItemViewerIds(workspaceUserIds, cycleContexts, sessionContexts),
    [workspaceUserIds, cycleContexts, sessionContexts],
  );

  const workspaceMemberIds = useMemo(() => new Set(workspaceUserIds), [workspaceUserIds]);
  const externalViewerCount = useMemo(
    () => allViewerIds.filter((id) => !workspaceMemberIds.has(id)).length,
    [allViewerIds, workspaceMemberIds],
  );

  return {
    loading: usersLoading,
    workspaceMemberCount: workspaceUsers.length,
    totalViewerCount: allViewerIds.length,
    externalViewerCount,
    cycleContexts,
    sessionContexts,
    allViewerIds,
  };
}
